import React, { useRef, useState } from "react";
import { Mic, Square, Loader2, Sparkles } from "lucide-react";
import { voice } from "@/lib/api";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";

/**
 * Voice recorder: press to record → stop → transcribe → parse into structured quote.
 * onParsed: (data) => void where data is VoiceQuoteExtracted
 */
export default function VoiceRecorder({ onParsed, primaryCurrency = "USD" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");
  const [transcript, setTranscript] = useState("");
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await handleBlob(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const handleBlob = async (blob) => {
    try {
      setBusy(true);
      setPhase("Transcribing…");
      const { data } = await voice.transcribe(blob);
      const t = data.transcript || "";
      setTranscript(t);
      if (!t.trim()) {
        toast.warning("Nothing to transcribe. Try again.");
        setBusy(false);
        return;
      }
      setPhase("Building your quote…");
      const parsed = await voice.parse(t, primaryCurrency);
      onParsed?.(parsed.data);
      toast.success("Quote drafted from your voice");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Voice processing failed";
      toast.error(String(msg).slice(0, 200));
    } finally {
      setBusy(false);
      setPhase("");
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="uppercase text-xs tracking-[0.2em] font-semibold text-[#2563EB] mb-2">
            Speak the job
          </div>
          <h3 className="text-xl sm:text-2xl tracking-tight font-bold text-[#001F3F]" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            Voice → Quote in seconds
          </h3>
          <p className="text-sm text-[#64748B] mt-2 leading-relaxed">
            Describe the job, materials, and hours. RivetQuote drafts line items automatically.
          </p>
        </div>
        <Sparkles className="text-[#3BB2F6] shrink-0" size={28} />
      </div>

      <div className="mt-6 flex flex-col items-center">
        {!recording && !busy && (
          <button
            data-testid={IDS.editor.voiceBtn}
            onClick={start}
            className="relative h-24 w-24 rounded-full bg-[#2563EB] text-white flex items-center justify-center shadow-lg hover:bg-[#1D4ED8] transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-blue-300"
            aria-label="Start recording"
          >
            <Mic size={38} />
          </button>
        )}
        {recording && (
          <button
            data-testid={IDS.editor.voiceStop}
            onClick={stop}
            className="relative h-24 w-24 rounded-full bg-[#EF4444] text-white flex items-center justify-center shadow-lg animate-pulse"
            aria-label="Stop recording"
          >
            <Square size={30} fill="white" />
            <span className="absolute -inset-2 rounded-full border-2 border-red-300 animate-ping" />
          </button>
        )}
        {busy && (
          <div className="h-24 flex flex-col items-center justify-center">
            <Loader2 className="text-[#2563EB] animate-spin" size={38} />
            <div className="mt-3 text-sm text-[#64748B]">{phase}</div>
          </div>
        )}
        <div className="mt-4 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
          {recording ? "Listening…" : busy ? "" : "Tap the mic to start"}
        </div>
      </div>

      {transcript && !busy && (
        <div
          data-testid={IDS.editor.transcriptBox}
          className="mt-6 rounded-md border border-slate-200 bg-[#F8FAFC] p-4 text-sm text-[#334155] leading-relaxed"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2">
            Transcript
          </div>
          {transcript}
        </div>
      )}
    </div>
  );
}
